import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { USER_ASSETS } from "@/lib/monuments";

const EASE = [0.22, 1, 0.36, 1];

export default function Paradox() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1]);

  return (
    <section
      id="paradox"
      ref={ref}
      data-testid="paradox-section"
      className="relative py-20 md:py-28 border-t border-gold/10"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10 md:gap-14 items-center">
        {/* Image — left */}
        <div className="md:col-span-6 relative order-1">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.05 }}
            transition={{ duration: 1.1, ease: EASE }}
            className="relative aspect-[16/12] md:aspect-[16/11] overflow-hidden bg-surface border border-gold/15"
            data-testid="paradox-image"
          >
            <motion.img
              src={USER_ASSETS.paradox}
              alt="Visitors gathered before an intact ancient stone arch — the paradox of magnificent fragments."
              style={{ y: imgY, scale: imgScale }}
              className="w-full h-full object-cover"
              loading="lazy"
              draggable={false}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian/50 via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
              <span className="font-mono-accent text-[10px] text-bone/85 bg-obsidian/50 backdrop-blur-sm px-3 py-1.5 border border-bone/15">
                The Vision · Restored in situ
              </span>
            </div>
          </motion.div>

          <div className="absolute -top-8 -left-4 hidden md:block font-display text-[10rem] leading-none text-gold/10 select-none pointer-events-none">
            01
          </div>
        </div>

        {/* Content — right */}
        <div className="md:col-span-6 order-2 md:pl-2">
          <div className="flex items-center gap-3 font-mono-accent text-[11px] text-gold/80">
            <span>Chapter 01</span>
            <span className="w-8 h-px bg-gold/40" />
            <span className="text-bone/50">The Paradox</span>
          </div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 1, ease: EASE }}
            className="mt-6 font-display text-4xl md:text-6xl leading-[1.05] tracking-tight text-bone"
            data-testid="paradox-heading"
          >
            Five thousand years <br />
            <em className="text-gold not-italic">reduced to remnants.</em>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 1, ease: EASE, delay: 0.05 }}
            className="mt-6 text-bone/70 text-lg leading-relaxed max-w-xl"
          >
            India&rsquo;s monuments are the world&rsquo;s most magnificent
            fragments. Roofless sanctums. Faceless deities. Broken colonnades.
            We rely on imagination to fill the gaps — the living cities, the
            ceremonies, the marketplaces remain invisible.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 1, ease: EASE, delay: 0.15 }}
            className="mt-5 max-w-xl font-display italic text-2xl md:text-3xl text-bone leading-snug"
          >
            &ldquo;Every monument should once again tell its complete story.&rdquo;
          </motion.p>

          <div className="mt-8 grid grid-cols-3 gap-6 max-w-lg">
            {[
              { k: "3,691", v: "Protected sites" },
              { k: "48+", v: "UNESCO listed" },
              { k: "0%", v: "Told in full" },
            ].map((s, i) => (
              <motion.div
                key={s.v}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 + i * 0.08 }}
                className="border-t border-gold/20 pt-3"
              >
                <div className="font-display text-3xl text-bone tabular-nums">
                  {s.k}
                </div>
                <div className="mt-1 font-mono-accent text-[10px] text-bone/50">
                  {s.v}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
